import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { X } from 'lucide-react';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { PageContentEditor } from '@/components/content-generator/editor/pages/components';
import { usePageContentDetailQuery } from '@/hooks/content-generator/pages/use-page-content-hooks';
import { useUniversalPageMutations } from '@/hooks/universal-page/useUniversalPageMutations';
import { useUnifiedAutosave } from '@/hooks/editor/use-unified-autosave';
import { useConfirmationDialog } from '@/hooks/ui/use-confirmation-dialog';
import { Editor } from '@tiptap/react';
import DashboardCard from '@/components/ui/DashboardCard';
import PageHeader from '@/components/content-generator/editor/PageHeader';
import { useDocumentPDF } from '@/hooks/pdf/use-document-pdf';

interface SimplePageEditorModalProps {
  pageId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onPageDeleted?: (pageId: string) => void;
  workbookId?: string; // Optional workbook context for project pages
}

const SimplePageEditorModal: React.FC<SimplePageEditorModalProps> = ({
  pageId,
  isOpen,
  onClose,
  onPageDeleted,
  workbookId, // Accept optional workbook context
}) => {
  const { isMobile } = useIsMobile();
  const [editor, setEditor] = useState<Editor | null>(null);
  const [isDeletingPage, setIsDeletingPage] = useState(false);
  const { showConfirmation, ConfirmationDialog } = useConfirmationDialog();
  const { generatePDF, isGenerating: isGeneratingPDF } = useDocumentPDF();

  // Preserve the pageId to prevent it from becoming empty during save operations
  // but allow it to update when the prop changes (for different pages)
  const [stablePageId, setStablePageId] = useState(pageId);
  
  // Update stable pageId when prop changes (for opening different pages)
  useEffect(() => {
    if (pageId && pageId !== stablePageId) {
      setStablePageId(pageId);
    }
  }, [pageId, stablePageId]);

  // Fetch page data
  const { data: pageContent, isLoading } = usePageContentDetailQuery(stablePageId || '');
  
  // Universal mutations for content, title updates, and deletion - use stable pageId
  const { updatePageContent, updatePageTitle, deletePage } = useUniversalPageMutations({
    pageId: stablePageId || '',
    workbookId: workbookId, // Use provided workbookId (undefined for private pages)
  });

  // Autosave functionality
  const onSavePageContent = useCallback(async (content: string) => {
    if (!pageContent) return false;
    try {
      await updatePageContent(content);
      return true;
    } catch (error) {
      console.error('Error saving page content:', error);
      return false;
    }
  }, [pageContent, updatePageContent]);

  // Autosave functionality - no longer need saveState
  useUnifiedAutosave({
    editor,
    onSave: onSavePageContent,
    enabled: !!editor && !!pageContent,
    debounceMs: 2000,
    onError: (err) => { console.error("Autosave error", err); }
  });

  // Handle page title update
  const handleUpdatePageTitle = useCallback(async (id: string, title: string) => {
    try {
      await updatePageTitle(title);
    } catch (error) {
      console.error('Error updating page title:', error);
    }
  }, [updatePageTitle]);

  // Handle page deletion
  const handleDeletePage = useCallback(async () => {
    if (!pageContent) return;
    
    const confirmed = await showConfirmation({
      title: 'Delete Document',
      description: 'This document will be permanently deleted. This action cannot be undone.',
      confirmText: 'Delete Forever',
      cancelText: 'Cancel',
      confirmVariant: 'red',
      cancelVariant: 'green'
    });

    if (confirmed && pageId) {
      setIsDeletingPage(true);
      try {
        await deletePage();
        
        // Reset loading state immediately after successful deletion
        setIsDeletingPage(false);
        
        // Close modal after successful deletion
        onClose();
        
        // Notify parent component about deletion
        if (onPageDeleted) {
          onPageDeleted(pageId);
        }
      } catch (error) {
        console.error('Error deleting page:', error);
        setIsDeletingPage(false);
      }
    }
  }, [pageContent, pageId, deletePage, onClose, onPageDeleted, showConfirmation]);

  // Handle PDF download
  const handleDownloadPDF = useCallback(async () => {
    if (!pageContent || !editor) return;
    
    // Get the current HTML content from the editor
    const currentContent = editor.getHTML();
    
    await generatePDF({
      title: pageContent.title || 'Untitled Document',
      content: currentContent,
      type: pageContent.type,
      updated_at: pageContent.updated_at
    });
  }, [pageContent, editor, generatePDF]);

  // Reset loading state when modal closes or pageId changes
  useEffect(() => {
    if (!isOpen || !pageId) {
      setIsDeletingPage(false);
    }
  }, [isOpen, pageId]);

  // Reset editor state when pageId changes to prevent stale references
  useEffect(() => {
    setEditor(null);
  }, [pageId]);

  // Don't render if not open or no pageId
  if (!isOpen || !pageId) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent 
          showCloseButton={false}
          className={`
            h-screen w-screen md:max-w-[1200px] md:h-[90vh] md:w-[90vw]
            p-0 cursor-default
            ${isMobile ? '!top-0 !left-0 !transform-none !translate-x-0 !translate-y-0' : ''}
          `}
        >
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        showCloseButton={false}
        className={`
          h-screen w-screen md:max-w-[1200px] md:h-[90vh] md:w-[90vw]
          p-0 cursor-default
          ${isMobile ? '!top-0 !left-0 !transform-none !translate-x-0 !translate-y-0' : ''}
        `}
      >
        <DialogTitle className="sr-only">
          Edit Document: {pageContent?.title || 'Untitled Document'}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Document content editor for private documents
        </DialogDescription>
        
        {/* Constraint wrapper - manages height and overflow */}
        <div className="h-full overflow-hidden flex flex-col">
          {/* Close button - Exact same positioning as other lightboxes */}
          <div className="fixed top-4 right-6 z-50">
            <DialogClose className="h-8 w-8 flex items-center justify-center bg-background/90 hover:bg-background shadow-sm transition-all cursor-pointer">
              <X className="h-4 w-4" />
            </DialogClose>
          </div>
          
          {/* Title Bar Section - NEW glassmorphism header */}
          <div className="flex-shrink-0">
            <DashboardCard className="!p-3 md:!p-4">
              <PageHeader 
                pageContentId={pageId}
                workbookId={workbookId || ""} // Use provided workbookId or empty for private pages
                title={pageContent?.title || 'Untitled Document'}
                onDelete={handleDeletePage}
                onUpdateTitle={handleUpdatePageTitle}
                isDeleting={isDeletingPage}
                onGoBack={onClose} // Use onClose instead of navigation
                hideBackButton={true} // Hide back button in modal context
                onDownloadPDF={handleDownloadPDF}
                isGeneratingPDF={isGeneratingPDF}
              />
            </DashboardCard>
          </div>
          
          {/* Editor container - takes remaining space with proper overflow */}
          <div className="flex-1 overflow-auto">
            {/* Styled wrapper - EXACT same as project pages */}
            <div className="overflow-hidden bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm h-full">
              <PageContentEditor
                content={pageContent?.content || ''}
                onContentChange={() => {}} // Handled by autosave
                onEditorReady={setEditor}
                workbookId={workbookId} // Pass through workbookId (undefined for private pages)
                pageId={pageId}
                className="h-full w-full"
                key={pageId} // Add stable key
              />
            </div>
          </div>
        </div>
        
        {/* Confirmation Dialog */}
        <ConfirmationDialog />
      </DialogContent>
    </Dialog>
  );
};

export default SimplePageEditorModal; 